/**
 * Hook otimizado para gerenciar o estado e lógica da página de clientes
 * 
 * NOVA VERSÃO OTIMIZADA:
 * - Usa paginação infinita com useLeadsInfinite
 * - Busca server-side com debounce
 * - Filtros aplicados diretamente no banco
 * - Melhor performance com grandes volumes de dados
 * - Suporte a modo admin com filtro por clínica
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTagsData } from './useTagsData';
import { useEtapas } from './useEtapasData';
import { useToast } from './use-toast';
import { useClinica } from '@/contexts/ClinicaContext';
import { useLeadsInfinite, useLeadsForExport, useAvailableAds } from './useLeadsInfinite';
import { useDebounce } from './useDebounce';
import { supabase } from '@/integrations/supabase/client';
import { Lead } from './useSupabaseLeads';

// Interfaces para filtros
interface FilterState {
  status: string;
  tag: string;
  origem: string;
  servico: string;
  apenasAnuncios: boolean;
  adName: string;
  adPlatform: string;
  hasActiveFilters: boolean;
}

// Tipos para ordenação
type SortField = 'nome' | 'created_at' | 'email';
type SortOrder = 'asc' | 'desc';

// Interface para parâmetros do hook
interface UseClientsPageOptimizedProps {
  adminMode?: boolean;
  targetClinicaId?: string | null;
}

// Interface para dados de página de leads
interface LeadsPageData {
  leads: Lead[];
  nextPage: number | null;
  totalCount: number;
}

// Tamanho padrão da página
const DEFAULT_PAGE_SIZE = 50;

export const useClientsPageOptimized = ({ 
  adminMode = false, 
  targetClinicaId = null 
}: UseClientsPageOptimizedProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clinicaId, isLoading: carregandoClinica } = useClinica();
  // Admin usa a clínica escolhida no seletor; usuário comum usa a clínica do próprio login.
  const clinicaDaConsulta = adminMode ? targetClinicaId : clinicaId;

  // Estados para busca e filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  
  // Estados para modais
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState<Lead | null>(null);
  
  // Estados para filtros específicos
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [origemFilter, setOrigemFilter] = useState<string | null>(null);
  const [servicoFilter, setServicoFilter] = useState<string | null>(null);
  const [etapaFilter, setEtapaFilter] = useState<string | null>(null);
  
  // Estados para filtros de anúncios
  const [apenasAnunciosFilter, setApenasAnunciosFilter] = useState<boolean>(false);
  const [adNameFilter, setAdNameFilter] = useState<string | null>(null);
  const [adPlatformFilter, setAdPlatformFilter] = useState<string | null>(null);
  
  // Estados para exportação
  const [isExporting, setIsExporting] = useState(false);
  const [exportScope, setExportScope] = useState<'filtered' | 'all'>('filtered');
  const [exportProgress, setExportProgress] = useState({ loaded: 0, total: 0 });

  // Debounce da busca para evitar muitas requisições
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Buscar dados auxiliares
  const { data: tags = [], isLoading: tagsLoading } = useTagsData();
  const { data: etapas = [], isLoading: etapasLoading } = useEtapas();
  
  // Buscar anúncios e plataformas disponíveis
  const { 
    data: adsData, 
    isLoading: adsLoading 
  } = useAvailableAds({ adminMode, targetClinicaId: clinicaDaConsulta });

  // Hook principal otimizado para buscar leads
  const {
    data: leadsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: leadsLoading,
    refetch: refetchLeads,
    isError,
    error
  } = useLeadsInfinite({
    searchQuery: debouncedSearchQuery,
    tagId: tagFilter,
    origemLead: origemFilter,
    servicoInteresse: servicoFilter,
    etapaId: etapaFilter,
    apenasAnuncios: apenasAnunciosFilter,
    adName: adNameFilter,
    adPlatform: adPlatformFilter,
    sortField,
    sortOrder,
    pageSize: DEFAULT_PAGE_SIZE,
    adminMode,
    targetClinicaId: clinicaDaConsulta
  });

  /**
   * Hook para exportação com suporte a paginação por lotes
   * 
   * IMPORTANTE: 
   * - exportScope='all' ignora filtros e exporta TODOS os contatos da clínica
   * - exportScope='filtered' aplica os filtros atuais
   * - onProgress permite mostrar progresso durante exportação de grandes volumes
   */
  const {
    data: exportData,
    refetch: refetchExportData,
    isLoading: isLoadingExport
  } = useLeadsForExport({
    enabled: isExporting && Boolean(clinicaDaConsulta),
    exportScope, // 'all' ou 'filtered' - controla se aplica filtros ou não
    onProgress: (loaded, total) => setExportProgress({ loaded, total }), // Callback para progresso
    searchQuery: debouncedSearchQuery,
    tagId: tagFilter,
    origemLead: origemFilter,
    servicoInteresse: servicoFilter,
    etapaId: etapaFilter,
    apenasAnuncios: apenasAnunciosFilter,
    adName: adNameFilter,
    adPlatform: adPlatformFilter,
    sortField,
    sortOrder,
    adminMode,
    targetClinicaId: clinicaDaConsulta
  });

  // Sem o id da clínica a query fica desligada; isso precisa continuar como loading, não como lista vazia.
  const loading = !clinicaDaConsulta || (!adminMode && carregandoClinica) || leadsLoading || tagsLoading || etapasLoading || adsLoading;
  
  // Achatar dados paginados em uma lista única
  const allLeads = useMemo(() => {
    if (!leadsData?.pages) return [];
    return leadsData.pages.flatMap((page: LeadsPageData) => page.leads);
  }, [leadsData]);

  // Contar total de leads carregados vs total disponível
  const totalLeadsLoaded = allLeads.length;
  const totalLeadsAvailable = leadsData?.pages[0]?.totalCount || 0;
  const remainingLeads = Math.max(0, totalLeadsAvailable - totalLeadsLoaded);

  // Verificar se há filtros ativos
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      debouncedSearchQuery ||
      tagFilter ||
      origemFilter ||
      servicoFilter ||
      etapaFilter ||
      apenasAnunciosFilter ||
      adNameFilter ||
      adPlatformFilter
    );
  }, [debouncedSearchQuery, tagFilter, origemFilter, servicoFilter, etapaFilter, apenasAnunciosFilter, adNameFilter, adPlatformFilter]);

  // Obter valores únicos para filtros (dos leads já carregados)
  const uniqueOrigens = useMemo(() => {
    return [...new Set(allLeads.map(lead => lead.origem_lead).filter(Boolean))] as string[];
  }, [allLeads]);

  const uniqueServicos = useMemo(() => {
    return [...new Set(allLeads.map(lead => lead.servico_interesse).filter(Boolean))] as string[];
  }, [allLeads]);

  // Filtros no formato esperado pelos componentes
  const filters: FilterState = {
    status: etapaFilter || '',
    tag: tagFilter || '',
    origem: origemFilter || '',
    servico: servicoFilter || '',
    apenasAnuncios: apenasAnunciosFilter,
    adName: adNameFilter || '',
    adPlatform: adPlatformFilter || '',
    hasActiveFilters
  };

  // Handlers para filtros
  const handleFilterChange = useCallback((key: string, value: string | boolean) => {
    const isEmpty = !value || value === '';
    
    switch (key) {
      case 'status':
        setEtapaFilter(isEmpty ? null : value as string);
        break;
      case 'tag':
        setTagFilter(isEmpty ? null : value as string);
        break;
      case 'origem':
        setOrigemFilter(isEmpty ? null : value as string);
        break;
      case 'servico':
        setServicoFilter(isEmpty ? null : value as string);
        break;
      case 'apenasAnuncios':
        setApenasAnunciosFilter(value as boolean);
        // Limpar filtros específicos de anúncios se desativar o toggle
        if (!value) {
          setAdNameFilter(null);
          setAdPlatformFilter(null);
        }
        break;
      case 'adName':
        setAdNameFilter(isEmpty ? null : value as string);
        break;
      case 'adPlatform':
        setAdPlatformFilter(isEmpty ? null : value as string);
        break;
    }
    
    // Resetar seleções quando filtros mudarem
    setSelectedLeadIds([]);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setTagFilter(null);
    setOrigemFilter(null);
    setServicoFilter(null);
    setEtapaFilter(null);
    setApenasAnunciosFilter(false);
    setAdNameFilter(null);
    setAdPlatformFilter(null);
    setSelectedLeadIds([]);
  }, []);

  // Handlers para ordenação
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setSelectedLeadIds([]);
  }, [sortField, sortOrder]);

  // Handlers para seleção
  const handleSelectLead = useCallback((leadId: string) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedLeadIds.length === allLeads.length && allLeads.length > 0) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(allLeads.map(lead => lead.id));
    }
  }, [selectedLeadIds.length, allLeads]);

  // Handler para carregar mais dados
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handlers para operações CRUD
  const handleDeleteLead = useCallback(async (leadId: string) => {
    if (!confirm('Tem certeza que deseja deletar este lead?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Lead deletado com sucesso!",
      });

      // Remover da seleção se estava selecionado
      setSelectedLeadIds(prev => prev.filter(id => id !== leadId));
      
      refetchLeads();
    } catch (error: any) {
      console.error('Erro ao deletar lead:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao deletar lead. Tente novamente.",
        variant: "destructive",
      });
    }
  }, [toast, refetchLeads]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedLeadIds.length === 0) return;
    
    if (!confirm(`Tem certeza que deseja deletar ${selectedLeadIds.length} leads selecionados?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .in('id', selectedLeadIds);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `${selectedLeadIds.length} leads deletados com sucesso!`,
      });

      setSelectedLeadIds([]);
      refetchLeads();
    } catch (error: any) {
      console.error('Erro ao deletar leads:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao deletar leads. Tente novamente.",
        variant: "destructive",
      });
    }
  }, [selectedLeadIds, toast, refetchLeads]);

  const handleBulkStatusUpdate = useCallback(async (etapaId: string) => {
    if (selectedLeadIds.length === 0) return;

    try {
      const { error } = await supabase
        .from('leads')
        .update({ etapa_kanban_id: etapaId })
        .in('id', selectedLeadIds);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `${selectedLeadIds.length} leads atualizados com sucesso!`,
      });

      setSelectedLeadIds([]);
      refetchLeads();
    } catch (error: any) {
      console.error('Erro ao atualizar leads:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar leads. Tente novamente.",
        variant: "destructive",
      });
    }
  }, [selectedLeadIds, toast, refetchLeads]);

  /**
   * Handler para exportação de contatos com suporte a lotes
   * 
   * TIPOS DE EXPORTAÇÃO:
   * - 'all': Exporta TODOS os contatos da clínica (ignora filtros) - sem limite!
   * - 'filtered': Exporta contatos que atendem aos filtros atuais - sem limite!
   * - 'selected': Exporta apenas os contatos selecionados na tela
   * 
   * COMO FUNCIONA A EXPORTAÇÃO ILIMITADA:
   * O hook useLeadsForExport busca dados em lotes de 1000 registros
   * para contornar o limite padrão do Supabase/PostgREST.
   */
  const handleExportContacts = useCallback(async (exportType: 'all' | 'filtered' | 'selected' = 'filtered') => {
    setIsExporting(true);
    setExportProgress({ loaded: 0, total: 0 }); // Resetar progresso
    
    try {
      let leadsToExport: Lead[] = [];
      let fileName = 'contatos.csv';

      if (exportType === 'selected' && selectedLeadIds.length > 0) {
        // Exportar apenas selecionados (usa dados já carregados na memória)
        leadsToExport = allLeads.filter(lead => selectedLeadIds.includes(lead.id));
        fileName = 'contatos-selecionados.csv';
      } else {
        // Para 'all' ou 'filtered', configurar escopo e buscar via hook paginado
        // 'all' = ignora filtros, busca todos da clínica
        // 'filtered' = aplica filtros atuais
        setExportScope(exportType === 'all' ? 'all' : 'filtered');
        fileName = exportType === 'all' ? 'contatos-todos.csv' : 'contatos-filtrados.csv';
        
        // Toast informativo para exportações grandes
        toast({
          title: "Exportando...",
          description: "Buscando todos os contatos. Aguarde...",
        });
        
        // Aguardar um tick para o estado atualizar antes do refetch
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Buscar todos os dados usando paginação por lotes
        const result = await refetchExportData();
        leadsToExport = result.data?.leads || [];
      }

      if (leadsToExport.length === 0) {
        toast({
          title: "Aviso",
          description: "Nenhum contato encontrado para exportar.",
          variant: "destructive",
        });
        return;
      }

      // Gerar CSV com todos os campos relevantes
      // Nota: alguns campos como ad_platform podem vir do banco mas não estão na interface Lead
      const csvContent = [
        ['Nome', 'Email', 'Telefone', 'Origem', 'Serviço', 'Etapa', 'Anúncio', 'Plataforma', 'Data Criação'].join(','),
        ...leadsToExport.map(lead => {
          const etapa = etapas.find(e => e.id === lead.etapa_kanban_id);
          // Cast para any para acessar campos que existem no banco mas não na interface
          const leadData = lead as any;
          return [
            lead.nome || '',
            lead.email || '',
            lead.telefone || '',
            lead.origem_lead || '',
            lead.servico_interesse || '',
            etapa?.nome || '',
            lead.ad_name || '',
            leadData.ad_platform || '', // Campo existe no banco mas não na interface Lead
            lead.created_at ? new Date(lead.created_at).toLocaleDateString('pt-BR') : ''
          ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','); // Escapar aspas duplas corretamente
        })
      ].join('\n');

      // Download do arquivo
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM para Excel reconhecer UTF-8
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Sucesso",
        description: `${leadsToExport.length} contatos exportados com sucesso!`,
      });

    } catch (error: any) {
      console.error('Erro ao exportar:', error);
      toast({
        title: "Erro",
        description: "Erro ao exportar contatos. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setExportProgress({ loaded: 0, total: 0 });
    }
  }, [selectedLeadIds, allLeads, refetchExportData, etapas, toast]);

  // Handlers para modais
  const handleAddLead = useCallback(() => {
    setSelectedLeadForEdit(null);
    setIsLeadModalOpen(true);
  }, []);

  const handleEditLead = useCallback((lead: Lead) => {
    setSelectedLeadForEdit(lead);
    setIsLeadModalOpen(true);
  }, []);

  const handleSaveLead = useCallback(async (leadData: any) => {
    try {
      if (selectedLeadForEdit) {
        // Atualizar lead existente
        const updateData = {
          nome: leadData.nome,
          telefone: leadData.telefone,
          email: leadData.email,
          etapa_kanban_id: leadData.etapa_kanban_id,
          tag_id: leadData.tag_id,
          anotacoes: leadData.anotacoes,
          origem_lead: leadData.origem_lead,
          servico_interesse: leadData.servico_interesse,
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('leads')
          .update(updateData)
          .eq('id', selectedLeadForEdit.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Lead atualizado com sucesso!",
        });
      } else {
        // Criar novo lead
        const effectiveClinicaId = adminMode && targetClinicaId ? targetClinicaId : clinicaId;
        
        const newLeadData = {
          ...leadData,
          clinica_id: effectiveClinicaId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('leads')
          .insert([newLeadData]);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Lead criado com sucesso!",
        });
      }

      setIsLeadModalOpen(false);
      setSelectedLeadForEdit(null);
      refetchLeads();
    } catch (error: any) {
      console.error('Erro ao salvar lead:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao salvar lead. Tente novamente.",
        variant: "destructive",
      });
    }
  }, [selectedLeadForEdit, adminMode, targetClinicaId, clinicaId, toast, refetchLeads]);

  const handleOpenChat = useCallback((lead: Lead) => {
    navigate(`/chat?leadId=${lead.id}`);
  }, [navigate]);

  // Função para refresh dos dados
  const refreshData = useCallback(() => {
    refetchLeads();
    setSelectedLeadIds([]);
  }, [refetchLeads]);

  return {
    // Estados de loading
    loading,
    isError,
    error,
    
    // Dados
    leads: allLeads,
    filteredLeads: allLeads, // Já filtrados pelo servidor
    displayedLeads: allLeads,
    tags,
    etapas,
    uniqueOrigens,
    uniqueServicos,
    availableAds: adsData?.availableAds || [],
    availablePlatforms: adsData?.availablePlatforms || [],
    
    // Estados de seleção
    selectedLeadIds,
    selectedLead: selectedLeadForEdit,
    
    // Estados de modal
    isLeadModalOpen,
    setIsLeadModalOpen,
    
    // Estados de busca e filtros
    searchQuery,
    setSearchQuery,
    isFilterOpen,
    setIsFilterOpen,
    filters,
    hasActiveFilters,
    
    // Estados de ordenação
    sortConfig: { field: sortField, order: sortOrder },
    
    // Estados de paginação
    totalLeads: totalLeadsAvailable,
    displayedLeadsCount: totalLeadsLoaded,
    hasMoreLeads: hasNextPage,
    remainingLeadsCount: remainingLeads,
    isLoadingMore: isFetchingNextPage,
    
    // Estados de exportação
    isExporting: isExporting || isLoadingExport,
    
    // Handlers
    handleSort,
    handleFilterChange,
    clearFilters,
    handleSelectLead,
    handleSelectAll,
    handleLoadMore,
    handleDeleteLead,
    handleDeleteSelected,
    handleBulkStatusUpdate,
    handleExportContacts,
    handleAddLead,
    handleEditLead,
    handleSaveLead,
    handleOpenChat,
    refreshData,
    
    // Setters para compatibilidade
    setSelectedLeadIds,
    setSelectedLead: setSelectedLeadForEdit,
  };
};
